import React, { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, X, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Call Recap card — the closure moment.
 *
 * Opens as a soft modal when the FOH presses "Complete intake" OR when a
 * complete session has not yet had the recap delivered. The FOH reads the
 * generated lines back to the customer in a natural cadence — not a script.
 *
 * Two actions:
 *   - "Recap delivered — wrap up"  → POST /recap-confirm, then closes.
 *   - "Not yet — keep open"       → closes the modal without confirming.
 *
 * Deterministic. Re-fetchable. Replayable.
 */
export default function CallRecapCard({ sessionId, open, onClose, onDelivered }) {
  const [recap, setRecap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !sessionId) return;
    setLoading(true);
    setError(null);
    axios.get(`${API}/intake/sessions/${sessionId}/recap`)
      .then((r) => setRecap(r.data?.recap || null))
      .catch((e) => setError(e?.response?.data?.detail || "Could not load recap"))
      .finally(() => setLoading(false));
  }, [open, sessionId]);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await axios.post(`${API}/intake/sessions/${sessionId}/recap-confirm`);
      if (onDelivered) onDelivered();
      if (onClose) onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to confirm recap");
    } finally {
      setConfirming(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm px-4"
      data-testid="call-recap-card-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] overflow-hidden flex flex-col"
        data-testid="call-recap-card"
      >
        <header className="px-7 pt-6 pb-3 flex items-start justify-between border-b border-slate-100">
          <div>
            <span className="inline-flex items-center gap-1.5 text-[11.5px] uppercase tracking-[0.22em] text-teal-700 font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-500" />
              Closure · Recap
            </span>
            <h2 className="text-[22px] font-bold text-slate-900 mt-1.5 leading-tight">
              Read this back to the customer
            </h2>
            <p className="text-[14px] text-slate-500 mt-1.5 leading-snug">
              Confirms understanding. Closes the call professionally. Takes ~20 seconds.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 flex-shrink-0 mt-1"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="px-7 py-6 overflow-y-auto flex-1">
          {loading && (
            <div className="flex items-center gap-2 text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Generating recap…
            </div>
          )}

          {error && !loading && (
            <p className="text-red-700 text-[14px]" data-testid="recap-error">{error}</p>
          )}

          {recap && !loading && (
            <div className="space-y-4">
              <p
                className="text-[18px] text-slate-900 font-medium leading-relaxed italic"
                data-testid="recap-opening-line"
              >
                {recap.opening_line}
              </p>

              <ul className="space-y-3 pl-1" data-testid="recap-lines">
                {(recap.lines || []).map((line, i) => (
                  <li
                    key={i}
                    className="text-[17px] text-slate-800 leading-relaxed pl-4 border-l-2 border-teal-200"
                  >
                    {line}
                  </li>
                ))}
              </ul>

              {recap.closing_line && (
                <p className="text-[17px] text-slate-700 italic leading-relaxed pt-2">
                  <MessageCircle className="w-4 h-4 inline-block mr-1 text-teal-600 opacity-70 align-text-bottom" />
                  {recap.closing_line}
                </p>
              )}
            </div>
          )}
        </div>

        <footer className="px-7 py-4 border-t border-slate-100 bg-slate-50/60 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onClose}
            className="text-[14px] text-slate-600 hover:text-slate-900 font-medium"
            data-testid="recap-cancel-btn"
          >
            Not yet — keep open
          </button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={confirming || loading || !recap}
            className="bg-teal-700 hover:bg-teal-800 text-white h-11 px-6 text-[15px] font-semibold"
            data-testid="recap-confirm-btn"
          >
            {confirming ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4 mr-2" />
            )}
            Recap delivered — wrap up
          </Button>
        </footer>
      </div>
    </div>
  );
}
